import _ from 'lodash';
import { history } from 'umi';

import {
  getActivePortalConfig,
  getCurrentSession,
  getGlobalItem,
  removeGlobalItem,
  shouldApplyAppLoginContext,
} from '../auth/sessionManager';
import { SESSION_TYPES, STORAGE_KEYS } from '../auth/sessionConstants';
import { buildPortalPath, parsePortalPath, pathFromPortalConfig } from './portalPath';

const LOGIN_PATH_PREFIX = '/user/login';
// Paths whose own URL must never be captured as a post-login redirect target.
const AUTH_HANDOFF_PATH_PREFIXES = [LOGIN_PATH_PREFIX, '/confirmAccount'];

const isLoginPath = (pathname = '') =>
  AUTH_HANDOFF_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const isOAuthCallbackLocation = () => {
  const search = window.location.search || '';
  if (!search.includes('code=')) return false;
  // Microsoft sends `state=`; Google PKCE returns only `?code=` on `/`.
  if (search.includes('state=')) return true;
  if ((window.location.hash || '').includes('id_token=')) return true;
  return !isLoginPath(window.location.pathname || '');
};

/**
 * Same-origin http(s) path only. Rejects protocol-relative (`//evil`),
 * backslashes, and off-site URLs so post-login navigation cannot leave Automy.
 * Returns a relative target (`/path?q#h`) or null.
 */
export const sanitizePostLoginRedirect = (raw) => {
  if (raw == null || raw === '') return null;
  const value = String(raw).trim();
  if (!value) return null;

  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.origin !== window.location.origin) return null;

    const path = url.pathname || '/';
    if (path.startsWith('//') || path.includes('\\') || path.includes('@')) return null;
    if (isLoginPath(path)) return null;
    // `/p/:company/:portal` remints and would loop logout/401 back into the gateway.
    if (path === '/p' || path.startsWith('/p/')) return null;

    return path + url.search + url.hash;
  } catch (e) {
    return null;
  }
};

/**
 * Return URL already stored on the login screen, in history, or from the
 * OAuth handoff key. Used so a second bounce to login cannot drop the first.
 */
export const getPendingLoginRedirect = () => {
  const fromWindow = new URLSearchParams(window.location.search || '').get('redirect');
  const fromWindowSafe = sanitizePostLoginRedirect(fromWindow);
  if (fromWindowSafe) return fromWindowSafe;

  const fromHistoryQuery = sanitizePostLoginRedirect(history.location?.query?.redirect);
  if (fromHistoryQuery) return fromHistoryQuery;

  const historySearch = history.location?.search;
  if (historySearch) {
    const fromHistorySearch = sanitizePostLoginRedirect(
      new URLSearchParams(historySearch).get('redirect'),
    );
    if (fromHistorySearch) return fromHistorySearch;
  }

  // The OAuth handoff key must only apply on the login screen or the provider
  // callback. Portal auto-sign-in (`/p/...`) and in-app logins would otherwise
  // inherit a stale `/apps/:id` return URL from a previous visit.
  const pathname = window.location.pathname || '';
  if (isLoginPath(pathname) || isOAuthCallbackLocation()) {
    try {
      return sanitizePostLoginRedirect(getGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN));
    } catch (e) {
      return null;
    }
  }

  return null;
};

/**
 * Infer the URL the user should return to after authenticating.
 * Ignores `/` with no query/hash (the default landing) and OAuth callbacks
 * (those must use the stored return URL, not `?code=`).
 */
export const resolveReturnUrl = () => {
  const pathname = window.location.pathname || '';

  if (isLoginPath(pathname) || isOAuthCallbackLocation()) {
    return getPendingLoginRedirect();
  }

  if (pathname === '/' && !window.location.search && !window.location.hash) {
    return null;
  }

  return window.location.href;
};

/**
 * Build `/user/login` (or another auth pathname) with portal/app/redirect.
 *
 * @param {object} [options]
 * @param {string} [options.pathname='/user/login']
 * @param {boolean} [options.preserveRedirect=true]
 * @param {string|number} [options.portalId]
 * @param {string|number} [options.appId]
 * @param {string} [options.redirect] explicit return URL
 * @param {Record<string, string|number>} [options.extra]
 */
export const buildLoginUrl = ({
  pathname = LOGIN_PATH_PREFIX,
  preserveRedirect = true,
  portalId,
  appId,
  extra,
  redirect,
} = {}) => {
  const params = new URLSearchParams();

  if (portalId != null && portalId !== '') params.set('portal', String(portalId));
  if (appId != null && appId !== '') params.set('app', String(appId));

  if (extra && typeof extra === 'object') {
    Object.entries(extra).forEach(([key, value]) => {
      if (value != null && value !== '') params.set(key, String(value));
    });
  }

  if (preserveRedirect) {
    const target = sanitizePostLoginRedirect(
      redirect || resolveReturnUrl() || getPendingLoginRedirect(),
    );
    if (target) params.set('redirect', target);
  }

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

/**
 * Login URL after leaving a process portal. Prefers `?portal=` so branding
 * loads; falls back to `/p/:company/:portal` when only slugs are known.
 */
export const buildPortalLoginUrl = (portal, extra = {}) => {
  if (portal?.id) {
    return buildLoginUrl({
      ...extra,
      portalId: portal.id,
    });
  }
  return buildPortalPath(portal?.companyCode, portal?.portalCode) || buildLoginUrl(extra);
};

/**
 * Back-link from forgot/reset/confirm/2FA. Keeps ?app= and ?portal= so an
 * app or portal login does not drop into the Automy form.
 */
export const buildReturnLoginUrl = () => {
  let portalId;
  try {
    portalId = getActivePortalConfig()?.id;
  } catch (e) {}
  let appId;
  try {
    appId = new URLSearchParams(window.location.search || '').get('app');
  } catch (e) {}
  return buildLoginUrl({
    portalId,
    appId,
    preserveRedirect: true,
  });
};

/**
 * True when a second history.replace to login would drop an existing return URL
 * or repeat the same login location (token-expiry cleanup races).
 */
export const shouldSkipLoginRedirect = (nextUrl) => {
  const currentPath = history.location?.pathname || '';
  if (!isLoginPath(currentPath)) return false;

  const currentSearch = history.location?.search || window.location.search || '';
  const currentParams = new URLSearchParams(String(currentSearch).replace(/^\?/, ''));
  const nextQuery = nextUrl && nextUrl.includes('?') ? nextUrl.slice(nextUrl.indexOf('?') + 1) : '';
  const nextParams = new URLSearchParams(nextQuery);

  if (currentParams.get('redirect') && !nextParams.get('redirect')) return true;

  return currentPath === pathnameOf(nextUrl) && currentParams.toString() === nextParams.toString();
};

const pathnameOf = (url) => {
  if (!url) return LOGIN_PATH_PREFIX;
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
};

const readStashedPortalConfig = () => {
  try {
    const fromSession = getActivePortalConfig();
    if (pathFromPortalConfig(fromSession)) return fromSession;
  } catch (e) {}
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
    const parsed = raw ? JSON.parse(raw) : null;
    if (pathFromPortalConfig(parsed)) return parsed;
  } catch (e) {}
  return null;
};

/** `/p/:company/:portal` after a portal login — not Automy `/`. */
export const resolvePortalPostLoginPath = () => {
  let queryPortal = null;
  try {
    queryPortal = new URLSearchParams(window.location.search || '').get('portal');
  } catch (e) {}
  const session = getCurrentSession();
  const inPortalSession = session?.type === SESSION_TYPES.PORTAL;
  if (!queryPortal && !inPortalSession) return null;

  const config = readStashedPortalConfig();
  if (!config) return null;
  if (queryPortal) return pathFromPortalConfig(config, queryPortal);
  if (
    inPortalSession &&
    config.id &&
    session.resourceId &&
    String(config.id) !== String(session.resourceId)
  ) {
    return null;
  }
  return pathFromPortalConfig(config);
};

export const consumePostLoginRedirect = () => {
  const params = new URLSearchParams(window.location.search || '');
  const fromQuery = sanitizePostLoginRedirect(params.get('redirect'));
  const storedRedirect = sanitizePostLoginRedirect(
    getGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN),
  );
  const appId = params.get('app') || getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
  const applyApp = shouldApplyAppLoginContext(appId);
  const redirect = getPendingLoginRedirect();

  if (redirect || storedRedirect) {
    try {
      removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);
    } catch (e) {}
  }
  try {
    removeGlobalItem(STORAGE_KEYS.APP_OAUTH_PENDING);
  } catch (e) {}

  if (fromQuery) return fromQuery;
  // Password / OAuth app login must return to /apps/:id even when the
  // callback stripped ?redirect= (Google lands on /?code=).
  if (applyApp && appId) {
    if (storedRedirect && String(storedRedirect).startsWith(`/apps/${appId}`)) {
      return storedRedirect;
    }
    if (redirect && String(redirect).startsWith(`/apps/${appId}`)) return redirect;
    return `/apps/${String(appId)}`;
  }
  if (redirect && /^\/apps\//.test(redirect) && !applyApp) return null;
  // Portal password login enters via enterPortalAfterLogin (`/` + portal
  // menu). Returning `/p/` here remints and dumps the user on Automy home.
  return redirect || null;
};

export const applyPostLoginRedirect = (redirect, { forceReload = false } = {}) => {
  const navigate = (url) => {
    if (forceReload) window.location.replace(url);
    else history.replace(url);
  };

  const parsedPortal = typeof redirect === 'string' ? parsePortalPath(redirect) : null;
  const portalEntry = parsedPortal
    ? buildPortalPath(parsedPortal.companyCode, parsedPortal.portalCode)
    : null;
  const target = portalEntry || sanitizePostLoginRedirect(redirect);
  if (!target) {
    navigate('/');
    return;
  }

  navigate(target);
};

/**
 * Check if the user should be redirected after a logout / refresh token
 * @param {Response} response
 */
export const authRedirect = (response) => {
  if (!(response && !_.isEmpty(response) && _.get(response, 'accessToken'))) {
    return;
  }

  const redirect = consumePostLoginRedirect();
  const forceReload = typeof redirect === 'string' && /\/apps\/[^/?#]+/.test(redirect);
  applyPostLoginRedirect(redirect, { forceReload });
};
